import * as React from 'react';
import * as XLSX from 'xlsx';
import Layout from './layout/Layout';
import Button from "react-bootstrap/Button";
import { DataGrid } from '@mui/x-data-grid';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Modal from 'react-bootstrap/Modal';
import Form from 'react-bootstrap/Form';
import api from '../../../helper/api';
import { getServerURL } from '../../../helper/envConfig';
import Avatar from '@mui/material/Avatar';
import TextField from '@mui/material/TextField';

// Function to get the current date in YYYY-MM-DD format
const getCurrentDate = () => {
  const date = new Date();
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const valueOrEmpty = (...values) => {
  for (const value of values) {
    if (value !== undefined && value !== null && String(value).trim() !== '') {
      return String(value).trim();
    }
  }
  return '';
};

const formatMoney = (amount, currency = 'USD') => {
  const text = valueOrEmpty(amount);
  if (!text) return '';
  return /^\D/.test(text) ? text : `${currency || 'USD'} ${text}`;
};

const paidLabel = (order) => {
  if (Object.prototype.hasOwnProperty.call(order || {}, 'paid')) {
    return order.paid === true || order.paid === 1 || order.paid === 'true' ? 'Yes' : 'No';
  }
  return order?.transaction_object?.charges?.data?.[0]?.paid ? 'Yes' : 'No';
};

const orderStatusLabel = (status) => {
  const statusMap = {
    0: 'Incomplete',
    1: 'Confirmed',
    2: 'Shipping',
    3: 'Complete',
    4: 'Canceled',
  };
  return statusMap[status] || valueOrEmpty(status);
};

const itemName = (order) => {
  if (order.line_type === 'flight') {
    return [order.flight_airline, order.flight_number, order.flight_route].filter(Boolean).join(' ') || order.product_name || 'Flight';
  }
  if (order.line_type === 'hotel') {
    return order.hotel_name || order.product_name || 'Hotel';
  }
  return order.product_name || 'Product';
};

const rowDetails = (order) => {
  if (order.line_type === 'flight') {
    return [
      order.flight_type ? `Type: ${order.flight_type}` : '',
      order.flight_source ? `Source: ${order.flight_source}` : '',
      order.flight_number ? `Flight: ${order.flight_number}` : '',
      order.flight_outbound_departure ? `Outbound departure: ${order.flight_outbound_departure}` : '',
      order.flight_outbound_arrival ? `Outbound arrival: ${order.flight_outbound_arrival}` : '',
      order.flight_return_departure ? `Return departure: ${order.flight_return_departure}` : '',
      order.flight_return_arrival ? `Return arrival: ${order.flight_return_arrival}` : '',
      order.flight_datetime ? `Flight time: ${order.flight_datetime}` : '',
      order.passenger_name ? `Passenger: ${order.passenger_name}` : '',
    ].filter(Boolean).join(' | ');
  }
  if (order.line_type === 'hotel') {
    return [
      order.room ? `Room: ${order.room}` : '',
      order.check_in ? `Check-in: ${order.check_in}` : '',
      order.check_out ? `Check-out: ${order.check_out}` : '',
      order.nights ? `Nights: ${order.nights}` : '',
      order.guests ? `Guests: ${order.guests}` : '',
      order.hotel_city ? `City: ${order.hotel_city}` : '',
    ].filter(Boolean).join(' | ');
  }
  return [
    order.merchant ? `Merchant: ${order.merchant}` : '',
    order.selected_color ? `Color: ${order.selected_color}` : '',
    order.selected_size ? `Size: ${order.selected_size}` : '',
  ].filter(Boolean).join(' | ');
};

const fullAddress = (order) => {
  const shipping = order.shipping_address_object || {};
  return order.shipping_address || shipping.full_address || [
    order.shipping_address_line1 || shipping.address,
    order.shipping_address_line2 || shipping.address_optional,
    order.shipping_city || shipping.city,
    order.shipping_state || order.state_name,
    order.shipping_zipcode || shipping.zipcode,
    order.shipping_country || order.country_name,
  ].filter(Boolean).join(', ');
};

const productImageUrl = (order) => {
  const direct = valueOrEmpty(order.product_image_url, order.product_image);
  if (direct) return direct;
  return order?.imageUrl && order?.product_id && order.product_images?.[0]?.file_name
    ? `${order.imageUrl}${order.product_id}/${order.product_images[0].file_name}`
    : '';
};

// Column definitions with export options
const columnDefinitions = [
  { field: 'transaction_id', headerName: 'Transaction ID', width: 300, exportable: true },
  { field: 'order_display_id', headerName: 'Order ID', width: 300, exportable: true },
  { field: 'line_type', headerName: 'Line Type', width: 130, exportable: true },
  { field: 'item', headerName: 'Item', width: 320, exportable: true },
  { field: 'details', headerName: 'Details', width: 480, exportable: true },
  { field: 'preferences', headerName: 'Preferences', width: 260, exportable: true },
  { field: 'worker_link', headerName: 'CLICK HERE TO BOOK', width: 420, exportable: true },
  { field: 'product_purchase_url', headerName: 'Product Purchase URL', width: 420, exportable: true },
  { field: 'product_image_url', headerName: 'Product Image URL', width: 420, exportable: true },
  { field: 'hotel_booking_link', headerName: 'Hotel Worker Booking Page', width: 420, exportable: true },
  { field: 'booking_com_hotel_link', headerName: 'Booking.com Hotel Link', width: 420, exportable: true },
  { field: 'flight_booking_link', headerName: 'Flight Worker Booking Page', width: 420, exportable: true },
  { field: 'stable_flight_search_link', headerName: 'Booking.com Flight Link', width: 420, exportable: true },
  { field: 'fee_breakdown', headerName: 'Fee Breakdown', width: 320, exportable: true },
  { field: 'worker_notes', headerName: 'Worker Notes', width: 520, exportable: true },
  { field: 'tracking_number', headerName: 'Tracking Number', width: 300, exportable: true, editable: true },
  { 
    field: 'product_image', 
    headerName: 'Product Image', 
    width: 150, 
    exportable: true, 
    renderCell: (params) => {
      if (!params) return <Avatar variant="rounded" />;
      
             // Simple frontend logic to get color-specific image
       const colorImage = (() => {
         const selectedColor = params.row.selected_color;
         const selectedSize = params.row.selected_size;
         const skuDetails = params.row.product_sku_details || [];
         const imageUrl = params.row.imageUrl;
         const productId = params.row.product_id;
        
                 
        
        if (selectedColor && skuDetails.length > 0 && imageUrl && productId) {
          // Find the SKU that matches both selected color AND size
          const matchingSku = skuDetails.find(sku => {
            const skuColor = sku.attrs?.[0]?.color;
            const skuSize = sku.attrs?.[0]?.size;
            
            return skuColor === selectedColor && skuSize === selectedSize;
          });
          
                      if (matchingSku && matchingSku.file_name) {
              const colorImageUrl = `${imageUrl}${productId}/${matchingSku.file_name}`;
              return colorImageUrl;
            }
          
          // If no exact match found, try to find by color only
          const colorOnlyMatch = skuDetails.find(sku => {
            const skuColor = sku.attrs?.[0]?.color;
            return skuColor === selectedColor;
          });
          
          if (colorOnlyMatch && colorOnlyMatch.file_name) {
            const colorImageUrl = `${imageUrl}${productId}/${colorOnlyMatch.file_name}`;
            return colorImageUrl;
          }
        }
        
        return params.row.product_image_url || params.value; // Fallback to default image
      })();
      
      return (
        <Avatar 
          src={colorImage} 
          variant="rounded" 
          style={{ width: '60px', height: '60px', objectFit: 'cover' }}
          onError={(e) => {
            e.target.src = params.value; // Fallback to default image
          }}
        />
      );
    }
  },
  { field: 'product_name', headerName: 'Product Name', width: 300, exportable: true },
  { field: 'hotel_name', headerName: 'Hotel Name', width: 240, exportable: true },
  { field: 'room', headerName: 'Room', width: 220, exportable: true },
  { field: 'hotel_city', headerName: 'Hotel City', width: 150, exportable: true },
  { field: 'hotel_dates', headerName: 'Hotel Dates', width: 180, exportable: true },
  { field: 'guests', headerName: 'Hotel Guests', width: 140, exportable: true },
  { field: 'hotel_price_per_night', headerName: 'Hotel Price/Night', width: 160, exportable: true },
  { field: 'hotel_total', headerName: 'Hotel Total', width: 140, exportable: true },
  { field: 'hotel_taxes_fees_note', headerName: 'Hotel Taxes/Fees Note', width: 420, exportable: true },
  { field: 'hotel_booking_link', headerName: 'Hotel Worker Booking Page', width: 300, exportable: true },
  { field: 'flight_source', headerName: 'Source', width: 160, exportable: true },
  { field: 'flight_number', headerName: 'Flight Number', width: 160, exportable: true },
  { field: 'flight_type', headerName: 'Flight Type', width: 140, exportable: true },
  { field: 'flight_route', headerName: 'Flight Route', width: 180, exportable: true },
  { field: 'flight_outbound_departure', headerName: 'Outbound Departure', width: 240, exportable: true },
  { field: 'flight_outbound_arrival', headerName: 'Outbound Arrival', width: 240, exportable: true },
  { field: 'flight_return_departure', headerName: 'Return Departure', width: 260, exportable: true },
  { field: 'flight_return_arrival', headerName: 'Return Arrival', width: 260, exportable: true },
  { field: 'flight_datetime', headerName: 'Flight Time', width: 320, exportable: true },
  { field: 'flight_return_date', headerName: 'Flight Return Date', width: 170, exportable: true },
  { field: 'preferences_summary', headerName: 'Seat/Bag Preferences', width: 300, exportable: true },
  { field: 'seat_bag_cost_note', headerName: 'Seat/Bag Cost Note', width: 420, exportable: true },
  { 
    field: 'product_variants', 
    headerName: 'Variants', 
    width: 200, 
    exportable: true,
    valueGetter: (params) => {
      if (!params || !params.row) return 'N/A';
      const color = params.row.product_color;
      const size = params.row.product_size;
      const variants = [];
      if (color) variants.push(`Color: ${color}`);
      if (size) variants.push(`Size: ${size}`);
      return variants.length > 0 ? variants.join(' | ') : 'N/A';
    },
         renderCell: (params) => {
       if (!params || !params.row) return <span style={{ color: '#999' }}>N/A</span>;
       
       const color = params.row.product_color;
       const size = params.row.product_size;
       const colorHex = params.row.color_hex_code;
                                 const colorImage = (() => {
            const selectedColor = params.row.selected_color;
            const selectedSize = params.row.selected_size;
            const skuDetails = params.row.product_sku_details || [];
            const imageUrl = params.row.imageUrl;
            const productId = params.row.product_id;
           
           if (selectedColor && skuDetails.length > 0 && imageUrl && productId) {
             // Find the SKU that matches both selected color AND size
             const matchingSku = skuDetails.find(sku => {
               const skuColor = sku.attrs?.[0]?.color;
               const skuSize = sku.attrs?.[0]?.size;
               
               return skuColor === selectedColor && skuSize === selectedSize;
             });
             
             if (matchingSku && matchingSku.file_name) {
               return `${imageUrl}${productId}/${matchingSku.file_name}`;
             }
             
             // If no exact match found, try to find by color only
             const colorOnlyMatch = skuDetails.find(sku => {
               const skuColor = sku.attrs?.[0]?.color;
               return skuColor === selectedColor;
             });
             
             if (colorOnlyMatch && colorOnlyMatch.file_name) {
               return `${imageUrl}${productId}/${colorOnlyMatch.file_name}`;
             }
           }
           
           return '';
         })();
       
       if (!color && !size) {
         return <span style={{ color: '#999' }}>N/A</span>;
       }
       
       return (
         <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
           {color && (
             <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
               {colorImage ? (
                 <img 
                   src={colorImage} 
                   alt={color}
                   style={{ 
                     width: '20px', 
                     height: '20px', 
                     objectFit: 'cover',
                     borderRadius: '2px',
                     border: '1px solid #ddd'
                   }}
                   onError={(e) => {
                     e.target.style.display = 'none';
                   }}
                 />
               ) : colorHex ? (
                 <div 
                   style={{ 
                     width: '12px', 
                     height: '12px', 
                     backgroundColor: colorHex, 
                     border: '1px solid #ddd',
                     borderRadius: '2px'
                   }} 
                 />
               ) : null}
               <span style={{ fontSize: '12px' }}>{color}</span>
             </div>
           )}
           {size && (
             <span style={{ fontSize: '12px', fontWeight: '500' }}>{size}</span>
           )}
         </div>
       );
     }
  },
  { 
    field: 'product_color', 
    headerName: 'Color', 
    width: 150, 
    exportable: true,
    renderCell: (params) => {
      if (!params) return <span>N/A</span>;
      
      const color = params.value;
      const colorHex = params.row?.color_hex_code;
      if (color && colorHex) {
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div 
              style={{ 
                width: '20px', 
                height: '20px', 
                backgroundColor: colorHex, 
                border: '1px solid #ddd',
                borderRadius: '4px'
              }} 
            />
            <span>{color}</span>
          </div>
        );
      }
      return <span>{color || 'N/A'}</span>;
    }
  },
  { 
    field: 'product_size', 
    headerName: 'Size', 
    width: 120, 
    exportable: true,
    renderCell: (params) => {
      if (!params) return <span>N/A</span>;
      
      const size = params.value;
      return <span style={{ fontWeight: size ? '500' : 'normal' }}>{size || 'N/A'}</span>;
    }
  },
  { field: 'quantity', headerName: 'Quantity', width: 100, exportable: true },
  { 
    field: 'individual_price', 
    headerName: 'Individual Price', 
    width: 150, 
    exportable: true,
    renderCell: (params) => {
      if (!params) return <span style={{ color: '#999' }}>N/A</span>;
      
      const price = params.value;
      if (price && price > 0) {
        return <span style={{ fontWeight: '500' }}>${parseFloat(price).toFixed(2)}</span>;
      }
      return <span style={{ color: '#999' }}>N/A</span>;
    }
  },
  { field: 'product_sku', headerName: 'SKU/Product ID', width: 150, exportable: true },
  { field: 'line_amount', headerName: 'Line Amount', width: 150, exportable: true },
  { field: 'fullname', headerName: 'Buyer Name', width: 170, exportable: true },
  { field: 'buyer_email', headerName: 'Email', width: 230, exportable: true },
  { field: 'buyer_phone', headerName: 'Phone', width: 150, exportable: true },
  { field: 'full_address', headerName: 'Full Address', width: 420, exportable: true },
  { field: 'address', headerName: 'Address', width: 130, exportable: true },
  { field: 'city', headerName: 'City', width: 130, exportable: true },
  { field: 'state_name', headerName: 'State', width: 130, exportable: true },
  { field: 'country_name', headerName: 'Country', width: 130, exportable: true },
  { field: 'zipcode', headerName: 'Zipcode', width: 130, exportable: true },
  { field: 'contact_no', headerName: 'Contact No', width: 130, exportable: true },
  { field: 'isPayed', headerName: 'Paid', width: 160, exportable: true },
  { field: 'order_status', headerName: 'Order Status', width: 160, exportable: true },
  { field: 'grand_total', headerName: 'Grand Total', width: 150, exportable: true },
  { field: 'created_at', headerName: 'Created At', width: 180, exportable: true },
];

const serverURL = getServerURL();

export default function DataTable({ embedded = false }) {
  const [rows, setRows] = React.useState([]);
  const [isLoading, setLoading] = React.useState(false);
  const [rowCount, setRowCount] = React.useState(0);
  const [paginationModel, setPaginationModel] = React.useState({
    page: 0,
    pageSize: 10,
  });
  const [showExportModal, setShowExportModal] = React.useState(false);
  const [selectedColumns, setSelectedColumns] = React.useState(
    columnDefinitions.reduce((acc, col) => {
      if (col.exportable) {
        acc[col.field] = true;
      }
      return acc;
    }, {})
  );
  const [editingTracking, setEditingTracking] = React.useState({});
  const [selectedRows, setSelectedRows] = React.useState([]);

  // Handle tracking number update
  const handleTrackingUpdate = async (orderId, newTrackingNumber) => {
    try {
      setLoading(true);
      const response = await api.postWithToken(`${serverURL}api/update-order-tracking`, {
        orderId: orderId,
        trackingNumber: newTrackingNumber
      });
      
      if (response.data.status) {
        // Update the local state
        setRows(prevRows => 
          prevRows.map(row => 
            row.id === orderId 
              ? { ...row, tracking_number: newTrackingNumber }
              : row
          )
        );
        setEditingTracking(prev => ({ ...prev, [orderId]: false }));
      }
    } catch (error) {
      console.log('Error updating tracking number:', error);
    } finally {
      setLoading(false);
    }
  };

    // Enhanced export function with selected rows only
  const handleExport = async () => {
    try {
      setLoading(true);
      
      // Use selected rows if any, otherwise use all current rows
      const rowsToExport = selectedRows.length > 0 ? selectedRows : rows;
      
      if (rowsToExport.length === 0) {
        alert('No rows to export. Please select at least one row or ensure data is loaded.');
        setLoading(false);
        return;
      }
      

      
            // Map the current rows data for export
      const mappedRows = rowsToExport.map((row) => ({
        id: row.id,
        transaction_id: row.transaction_id || '',
        order_display_id: row.order_display_id || '',
        tracking_number: row.tracking_number || '',
        product_image: row.product_image || '',
        product_name: row.product_name || '',
        hotel_name: row.hotel_name || '',
        room: row.room || '',
        hotel_city: row.hotel_city || '',
        hotel_dates: row.hotel_dates || '',
        guests: row.guests || '',
        hotel_price_per_night: row.hotel_price_per_night || '',
        hotel_total: row.hotel_total || '',
        hotel_taxes_fees_note: row.hotel_taxes_fees_note || '',
        hotel_booking_link: row.hotel_booking_link || '',
        booking_com_hotel_link: row.booking_com_hotel_link || '',
        flight_source: row.flight_source || '',
        flight_number: row.flight_number || '',
        flight_type: row.flight_type || '',
        flight_route: row.flight_route || '',
        flight_outbound_departure: row.flight_outbound_departure || '',
        flight_outbound_arrival: row.flight_outbound_arrival || '',
        flight_return_departure: row.flight_return_departure || '',
        flight_return_arrival: row.flight_return_arrival || '',
        flight_datetime: row.flight_datetime || '',
        flight_return_date: row.flight_return_date || '',
        preferences_summary: row.preferences_summary || '',
        seat_bag_cost_note: row.seat_bag_cost_note || '',
        product_color: row.product_color || '',
        product_size: row.product_size || '',
        quantity: row.quantity || 1,
        individual_price: row.individual_price || '',
        product_sku: row.product_sku || '',
        line_type: row.line_type || '',
        item: row.item || row.product_name || '',
        details: row.details || '',
        preferences: row.preferences || '',
        worker_link: row.worker_link || '',
        product_purchase_url: row.product_purchase_url || '',
        product_image_url: row.product_image_url || row.product_image || '',
        hotel_booking_link: row.hotel_booking_link || '',
        booking_com_hotel_link: row.booking_com_hotel_link || '',
        flight_booking_link: row.flight_booking_link || '',
        stable_flight_search_link: row.stable_flight_search_link || '',
        fee_breakdown: row.fee_breakdown || '',
        worker_notes: row.worker_notes || '',
        line_amount: row.line_amount || '',
        fullname: row.fullname || '',
        buyer_email: row.buyer_email || '',
        buyer_phone: row.buyer_phone || row.contact_no || '',
        full_address: row.full_address || '',
        address: row.address || '',
        city: row.city || '',
        state_name: row.state_name || '',
        country_name: row.country_name || '',
        zipcode: row.zipcode || '',
        contact_no: row.contact_no || '',
        isPayed: row.isPayed || '',
        order_status: row.order_status || '',
        grand_total: row.grand_total || '',
        created_at: row.created_at || '',
      }));

        // Filter columns based on selection
        const exportColumns = columnDefinitions.filter(col => selectedColumns[col.field]);
        
        const exportData = mappedRows.map(row => {
          const filteredRow = {};
          exportColumns.forEach(col => {
            filteredRow[col.headerName] = row[col.field] || '';
          });
          return filteredRow;
        });

        // Convert JSON to a worksheet
        const worksheet = XLSX.utils.json_to_sheet(exportData);

        // Create a new workbook and add the worksheet
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Orders');

        // Generate a unique filename with the current date
        const filename = `Orders_${getCurrentDate()}.xlsx`;

        // Download the Excel file
        XLSX.writeFile(workbook, filename);
        
        setShowExportModal(false);
        setLoading(false);
      } catch (error) {
        setLoading(false);
        console.log(error);
      }
    };

  // Custom tracking number cell renderer
  const TrackingNumberCell = (params) => {
    if (!params || !params.row) return <span>N/A</span>;
    
    const isEditing = editingTracking[params.row.id];
    
    if (isEditing) {
      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <TextField
            size="small"
            defaultValue={params.value}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handleTrackingUpdate(params.row.id, e.target.value);
              }
            }}
            autoFocus
          />
          <Button
            size="sm"
            variant="success"
            onClick={() => handleTrackingUpdate(params.row.id, document.querySelector(`input[data-row-id="${params.row.id}"]`)?.value || params.value)}
            style={{ fontSize: '10px', padding: '2px 6px', minWidth: 'auto' }}
          >
            Save
          </Button>
          <Button
            size="sm"
            variant="secondary"
            onClick={() => setEditingTracking(prev => ({ ...prev, [params.row.id]: false }))}
            style={{ fontSize: '10px', padding: '2px 6px', minWidth: 'auto' }}
          >
            Cancel
          </Button>
        </div>
      );
    }
    
    return (
      <div 
        style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer' }}
        onClick={() => setEditingTracking(prev => ({ ...prev, [params.row.id]: true }))}
      >
        <span>{params.value || 'Click to edit'}</span>
      </div>
    );
  };

  // Create columns with custom tracking number cell
  const columns = columnDefinitions.map(col => {
    if (col.field === 'tracking_number') {
      return {
        ...col,
        renderCell: TrackingNumberCell
      };
    }
    return col;
  });

  const fetchOrders = async (page, limit) => {
    try {
      setLoading(true);
      const response = await api.postWithToken(`${serverURL}api/user/orders?page=${page+1}&limit=${limit}`);
      if (response.data.status) {

        
                // Map the response data to match the columns
        const mappedRows = response.data.orders.map((order, index) => {
          const shipping = order.shipping_address_object || {};
          const currency = order.currency || 'USD';
          
                     const mappedRow = {
           id: order._id,
           transaction_id: order.transaction_id || '',
           order_display_id: order.order_display_id || '',
           line_type: order.line_type || 'product',
           item: itemName(order),
           details: rowDetails(order),
           preferences: order.preferences_summary || order.preferences?.summary || '',
           product_name: order.product_name || '',
           product_image: productImageUrl(order),
           product_image_url: order.product_image_url || productImageUrl(order),
           product_purchase_url: order.product_purchase_url || order.product_url || '',
           hotel_name: order.hotel_name || '',
           room: order.room || '',
           hotel_city: order.hotel_city || '',
           hotel_dates: [order.check_in, order.check_out].filter(Boolean).join(' - '),
           guests: order.guests || '',
           hotel_price_per_night: order.hotel_price_per_night || '',
           hotel_total: order.hotel_total || '',
           hotel_taxes_fees_note: order.hotel_taxes_fees_note || '',
           hotel_booking_link: order.hotel_booking_link || '',
           booking_com_hotel_link: order.booking_com_hotel_link || '',
           flight_source: order.flight_source || '',
           flight_number: order.flight_number || '',
           flight_type: order.flight_type || '',
           flight_route: order.flight_route || '',
           flight_outbound_departure: order.flight_outbound_departure || '',
           flight_outbound_arrival: order.flight_outbound_arrival || '',
           flight_return_departure: order.flight_return_departure || '',
           flight_return_arrival: order.flight_return_arrival || '',
           flight_datetime: order.flight_datetime || '',
           flight_return_date: order.flight_return_date || '',
           flight_booking_link: order.flight_booking_link || '',
           stable_flight_search_link: order.stable_flight_search_link || '',
           preferences_summary: order.preferences_summary || '',
           seat_bag_cost_note: order.seat_bag_cost_note || '',
           worker_link: order.worker_link || (order.line_type === 'hotel' ? order.hotel_booking_link : order.line_type === 'flight' ? order.flight_booking_link : (order.product_purchase_url || order.product_url)) || '',
           fee_breakdown: order.fee_breakdown || '',
           worker_notes: order.worker_notes || '',
            imageUrl: order.imageUrl || '',
            product_id: order.product_id || '',
            product_sku_details: order.product_sku_details || [],
            selected_color: order.selected_color || '',
            selected_size: order.selected_size || '',
            product_color: order.selected_color || order.color || '',
            product_size: order.selected_size || order.size || '',
          quantity: order.quantity || order.qty || 1,
          individual_price: order.individual_price || order.price || '',
          line_amount: formatMoney(order.line_amount || order.individual_price || order.price, currency),
          product_sku: order.product_id || order.sku || '',
          fullname: order.shipping_fullname || shipping.fullname || order.passenger_name || '',
          buyer_email: order.buyer_email || '',
          buyer_phone: order.buyer_phone || shipping.contact_no || '',
          full_address: fullAddress(order),
          address: order.shipping_address_line1 || shipping.address || '',
          city: order.shipping_city || shipping.city || '',
          state_name: order.state_name || '',
          country_name: order.country_name || '',
          zipcode: order.shipping_zipcode || shipping.zipcode || '',
          contact_no: order.buyer_phone || shipping.contact_no || '',
          sub_total_price: order.sub_total_price || '',
          tracking_number: order.tracking_number || '',
          payment_method: order?.transaction_object?.charges?.data?.[0]?.payment_method_details?.card?.brand || '',
          isPayed: paidLabel(order),
          order_status: orderStatusLabel(order.order_status),
          grand_total: formatMoney(order.grand_total, currency),
          created_at: order.created_at || '',
                     // Additional color and size details for debugging
           color_id: order.color_id || '',
           size_id: order.size_id || '',
           color_hex_code: order.color_hex_code || '',
           size_image: order.size_image || '',
                   };
          
          return mappedRow;
        });
                         setRowCount(response.data.totalOrders)
        setRows(mappedRows);
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.log(error);
    }
  };

  React.useEffect(() => {
    fetchOrders(paginationModel.page, paginationModel.pageSize);
  }, [paginationModel]);

  const content = (
    <>
      <div className='d-flex justify-content-between mb-2'>
        <Typography variant="h4">
          Spreadsheet / Excel
        </Typography>
        <Button className='btn btn-dark' onClick={() => setShowExportModal(true)} disabled={isLoading}>
          Export To Excel
        </Button>
      </div>

      <Paper sx={{ width: '100%' }}>
        <DataGrid
          loading={isLoading}
          rows={rows}
          rowCount={rowCount}
          columns={columns}
          pageSizeOptions={[5, 10]}
          checkboxSelection
          paginationMode="server"
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          onRowSelectionModelChange={(newSelectionModel) => {
            const selectedRowIds = newSelectionModel;
            const selectedRowData = rows.filter(row => selectedRowIds.includes(row.id));
            setSelectedRows(selectedRowData);
          }}
          sx={{ border: 0 }}
        />
      </Paper>

      {/* Export Column Selector Modal */}
      <Modal show={showExportModal} onHide={() => setShowExportModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Select Columns for Export</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
            {columnDefinitions.filter(col => col.exportable).map((column) => (
              <Form.Check
                key={column.field}
                type="checkbox"
                id={`export-${column.field}`}
                label={column.headerName}
                checked={selectedColumns[column.field]}
                onChange={(e) => setSelectedColumns(prev => ({
                  ...prev,
                  [column.field]: e.target.checked
                }))}
                className="mb-2"
              />
            ))}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowExportModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleExport} disabled={isLoading}>
            {isLoading ? 'Exporting...' : 'Export'}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );

  return embedded ? content : <Layout>{content}</Layout>;
}
